import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import IMAGES from "../../assets"; // Importing IMAGES which contains all icons
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api"; // Importing Google Maps
import { MAP_API_KEY } from "../../utils/Helpers"; // Assuming MAP_API_KEY is exported from Helpers

interface ChartData {
  name: string;
  uv: number;
  pv: number;
}

const ImpactDashboard: React.FC = () => {
  const [data, setData] = useState<ChartData[]>([]);
  const [cropData, setCropData] = useState<ChartData[]>([]);

  useEffect(() => {
    setData([
      { name: "Ships", uv: 4000, pv: 2400 },
      { name: "Pigs", uv: 3000, pv: 1398 },
      { name: "Goats", uv: 2000, pv: 9800 },
      { name: "cows", uv: 2780, pv: 3908 },
    ]);
  }, []);

  useEffect(() => {
    setCropData([
      { name: "Wheat", uv: 3200, pv: 5400 },
      { name: "Rice", uv: 4500, pv: 1500 },
      { name: "Corn", uv: 3500, pv: 2200 },
      { name: "Barley", uv: 2900, pv: 3200 },
    ]);
  }, []);

  // Pie chart data for Section 2
  const pieData = [
    { name: "Category 1", value: 400 },
    { name: "Category 2", value: 300 },
  ];

  const pieData2 = [
    { name: "Category 1", value: 400 },
    { name: "Category 2", value: 300 },
    { name: "Category 3", value: 300 },
    { name: "Category 4", value: 200 },
    { name: "Category 5", value: 100 },
  ];

  const pieData3 = [
    { name: "Category 1", value: 400 },
    { name: "Category 2", value: 300 },
    { name: "Category 3", value: 300 },
    { name: "Category 4", value: 200 },
    { name: "Category 5", value: 100 },
    { name: "Category 6", value: 50 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  return (
    <div className="w-screen h-screen py-16 px-4 md:px-24">
      {/* Main Content */}
      <div className="w-full min-h-screen bg-gray-100 p-4 flex flex-col">
        {/* Header */}
        <header className="mb-4">
          <div className="flex justify-between items-center">
            <input
              type="text"
              placeholder="Search..."
              className="p-2 border rounded w-1/3"
            />
          </div>
        </header>

        {/* Background for Boxes */}
        <div className="bg-[#67B89C] p-4 mb-6 rounded-tl-lg rounded-tr-lg relative h-[100px]">
          <div className="absolute top-2 left-2">
            <img src={IMAGES.logo} alt="SunCulture Logo" className="w-32" />
          </div>

          <div className="absolute top-2 right-2">
            <button className="bg-[#f7dc6f] text-black py-1 px-3 rounded-lg font-semibold text-sm">
              READ MORE
            </button>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 shadow rounded-lg flex items-center justify-center relative h-32 mt-12">
              <div className="absolute top-0 left-0 w-full text-center font-bold p-2">
                Demographics
              </div>
              <div className="ml-4 text-xl font-bold">123</div>
              <div className="absolute top-2 right-2 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                {/* Profile Icon */}
                <img src={IMAGES.user} alt="user Logo" className="w-32" />
              </div>
            </div>
            <div className="bg-white p-4 shadow rounded-lg flex items-center justify-center relative h-32 mt-12">
              <div className="absolute top-0 left-0 w-full text-center font-bold p-2">
                Social Impacts
              </div>
              <div className="ml-4 text-xl font-bold">456</div>
              <div className="absolute top-2 right-2 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                <img
                  src={IMAGES.social}
                  alt="social-impact"
                  className="w-6 h-6"
                />
              </div>
            </div>
            <div className="bg-white p-4 shadow rounded-lg flex items-center justify-center relative h-32 mt-12">
              <div className="absolute top-0 left-0 w-full text-center font-bold p-2">
                Sources of Income
              </div>
              <div className="ml-4 text-xl font-bold">789</div>
              <div className="absolute top-2 right-2 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                <img src={IMAGES.income} alt="income" className="w-6 h-6" />
              </div>
            </div>
            <div className="bg-white p-4 shadow rounded-lg flex items-center justify-center relative h-32 mt-12">
              <div className="absolute top-0 left-0 w-full text-center font-bold p-2">
                Expenses
              </div>
              <div className="ml-4 text-xl font-bold">101</div>
              <div className="absolute top-2 right-2 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                <img
                  src={IMAGES.spending}
                  alt="spending 4"
                  className="w-6 h-6"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Data display  */}
        <div className="space-y-6" rounded-lg>
          {/* Google Map Section */}
          <div className="bg-white p-4 shadow rounded-lg mb-6 mt-28">
            {/* Location Text */}
            <div className="text-center mb-4">
              <span className="text-dark-blue font-bold">Locations</span>
            </div>

            <LoadScript googleMapsApiKey={MAP_API_KEY || "MAP_API_KEY"}>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "300px" }}
                center={{ lat: 37.7749, lng: -122.4194 }}
                zoom={10}
              >
                <Marker position={{ lat: 37.7749, lng: -122.4194 }} />
              </GoogleMap>
            </LoadScript>
          </div>

          {/* Chart Section */}
          <div className="bg-white  shadow rounded-lg mb-6 flex flex-col items-center">
            {/* Social Impacts Text at the Top Center */}
            <div
              className="w-full h-10 flex items-center rounded justify-center"
              style={{ backgroundColor: "#2F6D44" }}
            >
              <span className="text-white font-bold ">Social Impacts</span>
            </div>

            <div className="relative flex items-center w-full">
              {/* Pie Chart Section on the Left */}
              <div className="w-1/3">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart style={{ left: "30px", top: "-30px" }}>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === 0 ? "#A1DAB8" : "#4D9F72"}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Expense Reduction - Positioned Absolutely */}
              <div
                className="absolute text-sm"
                style={{ left: "170px", top: "180px" }}
              >
                <span className="font-medium">
                  Expense <br />
                  Reduction
                  <br />
                  (Fuel)
                </span>
              </div>

              {/* Graph data explanations */}
              <div
                className="absolute text-sm"
                style={{ left: "330px", top: "50px" }}
              >
                <span className="font-medium">
                  Income <br />
                  Increase
                  <br />
                  (Yield)
                </span>
              </div>
              <div
                className="absolute text-sm text-center font-bold"
                style={{ left: "30px", top: "80px", fontSize: "24px" }}
              >
                <span>$7</span>
                <hr className="border-t-4 border-black my-2" />
                <div className="text-base font-medium ">
                  Impact <br /> per month
                </div>
              </div>
              {/* Other box statistics for section 2 div*/}

              <div
                className="absolute font-bold text-center flex items-center justify-center"
                style={{
                  left: "600px",
                  top: "20px",
                  width: "80px",
                  height: "50px",
                  backgroundColor: "#A1DAB8",
                  color: "#000",
                  fontSize: "18px",
                  border: "1px solid black",
                }}
              >
                95%
              </div>

              <div
                className="absolute text-sm"
                style={{ left: "690px", top: "20px" }}
              >
                <span className="font-medium">
                  Improved <br />
                  Quality of
                  <br />
                  life
                </span>
              </div>

              <div
                className="absolute font-bold text-center flex items-center justify-center"
                style={{
                  left: "600px",
                  top: "150px",
                  width: "80px",
                  height: "50px",
                  backgroundColor: "#A1DAB8",
                  color: "#000",
                  fontSize: "18px",
                  border: "1px solid black",
                }}
              >
                91%
              </div>

              <div
                className="absolute text-sm"
                style={{ left: "690px", top: "150px" }}
              >
                <span className="font-medium">
                  Improved <br />
                  way of
                  <br />
                  farming
                </span>
              </div>

              <div
                className="absolute font-bold text-center flex items-center justify-center"
                style={{
                  left: "900px",
                  top: "20px",
                  width: "80px",
                  height: "50px",
                  backgroundColor: "#A1DAB8",
                  color: "#000",
                  fontSize: "18px",
                  border: "1px solid black",
                }}
              >
                95%
              </div>

              <div
                className="absolute text-sm"
                style={{ left: "990px", top: "20px" }}
              >
                <span className="font-medium">
                  Increased <br />
                  Productivity
                </span>
              </div>

              <div
                className="absolute font-bold text-center flex items-center justify-center"
                style={{
                  left: "900px",
                  top: "150px",
                  width: "80px",
                  height: "50px",
                  backgroundColor: "#A1DAB8",
                  color: "#000",
                  fontSize: "18px",
                  border: "1px solid black",
                }}
              >
                95%
              </div>

              <div
                className="absolute text-sm"
                style={{ left: "990px", top: "150px" }}
              >
                <span className="font-medium">
                  Increased <br />
                  Money
                  <br />
                  Earned
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Left Side - Vertical Bar Chart */}
            <div className="bg-white  shadow rounded w-[800px] h-[430px]">
              <div
                className="w-full h-10 flex items-center rounded justify-center"
                style={{ backgroundColor: "#80E5A0" }}
              >
                <h3 className="text-lg font-bold mb-2 text-center">
                  Farmers Production
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4 items-center">
                {/* Left Side: Farmers & Annual Yield Divs */}
                <div className="grid grid-cols-1 gap-4 text-center p-2">
                  {[{ title: "Total Cattles" }, { title: "Total Crops" }].map(
                    (item, index) => (
                      <div
                        key={index}
                        className="rounded shadow flex flex-col text-center"
                        style={{
                          width: "250px",
                          height: "180px",
                          backgroundColor: "#d0ece7",
                        }}
                      >
                        {/* Title Section (Header) */}
                        <div
                          style={{
                            backgroundColor: "#45b39d",
                            height: "40px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <p className="font-bold">{item.title}</p>
                        </div>

                        {/* Number Section (Centered in Box) */}
                        <div
                          style={{
                            flexGrow: 1, // Takes up remaining space
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <p className="font-bold text-2xl">
                            {Math.floor(Math.random() * (10 - 5 + 1)) + 5}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Right Side: Bar Chart */}
                <div className="relative flex justify-center p-2">
                  <ResponsiveContainer
                    width="100%"
                    height={180}
                    style={{
                      position: "absolute",
                      top: "-170px",
                      left: "-70px",
                    }}
                  >
                    <BarChart data={data} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="uv" fill="#A1DAB8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Right Side: Crops Chart */}
                <div className="relative flex justify-center p-2">
                  <ResponsiveContainer
                    width="100%"
                    height={180}
                    style={{
                      position: "absolute",
                      top: "-200px",
                      left: "290px",
                    }}
                  >
                    <BarChart data={cropData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="uv" fill="#A1DAB8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Right Side - Pie Chart */}

            <div className="bg-white shadow rounded w-[700px] h-[430px] relative">
              <div
                className="w-full h-10 flex items-center rounded justify-center"
                style={{ backgroundColor: "#2F6D44" }}
              >
                <h3 className="text-white font-bold mb-2 text-center">
                  Types of Expenses
                </h3>
              </div>

              <div className="flex justify-center items-center relative">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData2} // Ensure pieData2 has 5 parts
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                    >
                      {pieData2.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            index === 0
                              ? "#2F6D44" // Dark green
                              : index === 1
                              ? "#A1DAB8" // Light green
                              : index === 2
                              ? "#4B9E9F" // Dark teal
                              : index === 3
                              ? "#72C7A1" // Lighter teal
                              : index === 4
                              ? "#A1D8C7" // Very light teal
                              : ""
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                {/* Text labels around the chart */}
                <div
                  className="absolute text-sm"
                  style={{ left: "25%", top: "30%" }}
                >
                  <span className="font-medium">Loans</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "65%", top: "20%" }}
                >
                  <span className="font-medium">School</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "30%", top: "70%" }}
                >
                  <span className="font-medium">Rent</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "68%", top: "55%" }}
                >
                  <span className="font-medium">Food</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "58%", top: "80%" }}
                >
                  <span className="font-medium">Farm Inputs</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Left Side - Pie Chart (Smaller Box) */}
            <div className="bg-white shadow rounded w-2/5 relative">
              <div
                className="w-full h-10 flex items-center rounded justify-center"
                style={{ backgroundColor: "#DAF7A6" }}
              >
                <h3 className="text-lg font-bold mb-2 text-center">
                  Sources Income
                </h3>
              </div>

              <div className="flex justify-center items-center relative">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData3} // Ensure pieData3 has 6 parts
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                    >
                      {pieData3.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            index === 0
                              ? "#DFFF00" // Yellow
                              : index === 1
                              ? "#DAF7A6" // Light green
                              : index === 2
                              ? "#FF5733" // Red-orange
                              : index === 3
                              ? "#C70039" // Dark red
                              : index === 4
                              ? "#900C3F" // Darker red
                              : index === 5
                              ? "#581845" // Purple
                              : ""
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                {/* Text labels around the chart */}
                <div
                  className="absolute text-sm"
                  style={{ left: "20%", top: "30%" }}
                >
                  <span className="font-medium">Services</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "65%", top: "20%" }}
                >
                  <span className="font-medium">Remit</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "55%", top: "80%" }}
                >
                  <span className="font-medium">Agriculture</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "70%", top: "40%" }}
                >
                  <span className="font-medium">Commerce</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "68%", top: "65%" }}
                >
                  <span className="font-medium">Pension</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "70%", top: "52%" }}
                >
                  <span className="font-medium">Wage Private</span>
                </div>
                <div
                  className="absolute text-sm"
                  style={{ left: "20%", top: "70%" }}
                >
                  <span className="font-medium">Wage Gov.</span>
                </div>
              </div>
            </div>

            {/* Right Side - Larger Box with Four Equal Boxes */}
            <div className="w-3/5 bg-white shadow rounded flex flex-col">
              {/* Section Header */}
              <div
                className="w-full h-10 flex items-center justify-center rounded"
                style={{ backgroundColor: "#008080" }}
              >
                <h3 className="text-white font-bold text-center">
                  Demographics
                </h3>
              </div>

              {/* Grid Section */}
              <div className="grid grid-cols-2 gap-4 text-center p-2">
                {[
                  { title: "# of Farmers" },
                  { title: "Monthly Income" },
                  { title: "Annual Yield" },
                  { title: "Expense Reduction" },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="rounded shadow flex flex-col w-full h-36"
                    style={{ backgroundColor: "#d0ece7" }}
                  >
                    {/* Title Section (Header) */}
                    <div
                      style={{
                        backgroundColor: "#45b39d",
                        height: "30px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <p className="font-bold">{item.title}</p>
                    </div>

                    {/* Number Section (Centered in Box) */}
                    <div
                      style={{
                        flexGrow: 1, // Takes up remaining space
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <p className="font-bold text-2xl">
                        {Math.floor(Math.random() * (707 - 290 + 1)) + 290}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Footer */}
          <footer className="mt-auto bg-[#616a6b] text-white p-2 text-center rounded flex justify-between items-center">
            {/* Left side: Gray and Light Gray boxes concatenated */}
            <div className="flex flex-row w-1/3">
              <div className="bg-gray-600 text-white p-4 w-full">
                {/* Left Box - Gray Background */}
                <p className="font-bold text-2xl">Token</p>
              </div>
              <div className="bg-gray-300 text-black p-4 w-full">
                {/* Left Box - Light Gray Background */}
                <p className="font-bold text-2xl">T12</p>
              </div>
            </div>

            {/* Space between the two sets */}
            <div className="w-1/3"></div>

            {/* Right side: Gray and Light Gray boxes concatenated */}
            <div className="flex flex-row w-1/3">
              <div className="bg-gray-600 text-white p-4 w-full">
                {/* Right Box - Gray Background */}
                <p className="font-bold text-2xl">Carbon Credit</p>
              </div>
              <div className="bg-gray-300 text-black p-4 w-full">
                {/* Right Box - Light Gray Background */}
                <p className="font-bold text-2xl">3878</p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ImpactDashboard;
